/**
 * Patient availability → blocked time ranges for the displayed day.
 *
 * Replaces the legacy "patient availability column" (reserved resource id "0",
 * built in CalendarAdapters.toCalendarResources): patient availability is
 * therapist-INDEPENDENT, and in day view every column is the same day, so a
 * blocked time applies identically to every column. Rendering it as one
 * day-spanning band (overlays/PatientBlockedBand) instead of a synthetic
 * column means selecting a patient can never re-index the therapist columns —
 * which is what made the column appear/disappear with a full-grid reflow.
 *
 * Two data sources, merged (same as the legacy column):
 *  - `PatientAvailabilityDto[]` — WEEKLY unavailability blocks
 *    (dayOfWeek + startTime/endTime), scoped by a validity window.
 *    A day whose blocks all lack times is blocked entirely.
 *  - `TimeSlotDto[]`  — DATE-SPECIFIC blocking slots (concrete start/end
 *    datetimes, e.g. patient vacation) from the unavailability-type-by-date
 *    query; the legacy path rendered these as events inside column "0".
 *
 * Pure module — no React, fully unit-testable.
 */
import { PatientAvailabilityDto, TimeSlotDto } from "../../../api-client";
import { MCGridConfig, MCTimeRange } from "./types";

/**
 * Minutes-of-day from a DTO time value. The backend serializes weekly block
 * times as full datetimes (the legacy adapter does `moment(startTime)
 * .format("HH:mm")`), but be liberal: accept bare "HH:mm[:ss]" strings too.
 * Returns null for absent/unparseable values so callers can distinguish
 * "no time" (= whole-day block) from midnight.
 */
const timeOfDayMin = (value: string | null | undefined): number | null => {
  if (!value) return null;
  const asTime = /^(\d{1,2}):(\d{2})/.exec(value);
  if (asTime && !value.includes("T") && !value.includes("-")) {
    return Number(asTime[1]) * 60 + Number(asTime[2]);
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.getHours() * 60 + parsed.getMinutes();
};

/** Local YYYY-MM-DD, for date-only validity comparisons (legacy parity). */
const dateKey = (d: Date): string => {
  const m = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
};

/** Merge overlapping/touching ranges into a sorted minimal set. */
const mergeRanges = (ranges: MCTimeRange[]): MCTimeRange[] => {
  const sorted = [...ranges].sort((a, b) => a.startMin - b.startMin);
  const merged: MCTimeRange[] = [];
  for (const range of sorted) {
    const last = merged[merged.length - 1];
    if (last && range.startMin <= last.endMin) {
      last.endMin = Math.max(last.endMin, range.endMin);
    } else {
      merged.push({ ...range });
    }
  }
  return merged;
};

export interface PatientBlockedInput {
  /** Weekly unavailability blocks for the selected patient. */
  availabilities: ReadonlyArray<PatientAvailabilityDto> | undefined;
  /** Date-specific blocking slots (unavailability types by date). */
  blockingSlots: ReadonlyArray<TimeSlotDto> | undefined;
  /** Displayed day. */
  date: Date;
  cfg: Pick<MCGridConfig, "dayStartMin" | "dayEndMin">;
}

/**
 * Blocked ranges for the displayed day, clamped to the visible window,
 * merged and sorted. Empty array = patient fully available (or no patient).
 *
 * Cross-midnight guard: a block whose end resolves at/before its start is
 * treated as running to the end of the visible day rather than dropped or
 * inverted — the legacy column silently produced degenerate business hours
 * for these; here the failure mode is "over-blocked", which a planner will
 * notice and question, instead of "silently bookable".
 */
export const buildPatientBlockedRanges = ({
  availabilities,
  blockingSlots,
  date,
  cfg,
}: PatientBlockedInput): MCTimeRange[] => {
  const ranges: MCTimeRange[] = [];
  const { dayStartMin, dayEndMin } = cfg;
  const displayedKey = dateKey(date);
  const weekday = date.getDay();

  const pushClamped = (startMin: number, endMin: number) => {
    const start = Math.max(startMin, dayStartMin);
    const end = Math.min(endMin <= startMin ? dayEndMin : endMin, dayEndMin);
    if (end > start) ranges.push({ startMin: start, endMin: end });
  };

  // ── Weekly blocks ────────────────────────────────────────────────────────
  const dayBlocks = (availabilities ?? []).filter((block) => {
    if (block.dayOfWeek !== weekday) return false;
    if (block.validityStart && dateKey(new Date(block.validityStart)) > displayedKey) return false;
    if (block.validityEnd && dateKey(new Date(block.validityEnd)) < displayedKey) return false;
    return true;
  });

  if (dayBlocks.length > 0) {
    // Legacy semantics: if NO block for the day carries a time, the whole day
    // is blocked; otherwise only the timed blocks count (untimed ones are
    // ignored, matching toCalendarResources' `startTime && endTime` filter).
    const hasTimedBlocks = dayBlocks.some((block) => block.startTime || block.endTime);
    if (!hasTimedBlocks) {
      ranges.push({ startMin: dayStartMin, endMin: dayEndMin });
    } else {
      for (const block of dayBlocks) {
        const start = timeOfDayMin(block.startTime);
        const end = timeOfDayMin(block.endTime);
        if (start == null || end == null) continue;
        pushClamped(start, end);
      }
    }
  }

  // ── Date-specific blocking slots ─────────────────────────────────────────
  for (const slot of blockingSlots ?? []) {
    if (!slot.start || !slot.end) continue;
    const start = new Date(slot.start);
    const end = new Date(slot.end);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) continue;
    const startsToday = dateKey(start) === displayedKey;
    const endsToday = dateKey(end) === displayedKey;
    // Multi-day slots (e.g. a vacation) must block the covered middle days,
    // not just the edges.
    const dayStart = new Date(date);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(dayStart);
    dayEnd.setDate(dayEnd.getDate() + 1);
    if (end <= dayStart || start >= dayEnd) continue;
    const startMin = startsToday ? start.getHours() * 60 + start.getMinutes() : dayStartMin;
    const endMin = endsToday ? end.getHours() * 60 + end.getMinutes() : dayEndMin;
    pushClamped(startMin, endMin);
  }

  return mergeRanges(ranges);
};
